/**
 * Phase 9.7.20 — 🎖️ "Now Featured" Share Modal
 * --------------------------------------------
 * Orchestrates layout selection + offscreen render + html-to-image
 * rasterization + Web Share / download for the NowFeaturedCard.
 *
 * Reuses the Cloudinary-safe `imageToDataUri` pipeline from the
 * existing FeaturedRecapCard so cover/avatar bytes always embed.
 *
 * Trigger props:
 *   open        — controlled visibility
 *   onOpenChange — close handler
 *   submission  — track doc with submission_id, track_title, cover, etc.
 *   artist      — { artist_name, profile_photo, picture }
 *
 * The modal pre-fetches /api/metrics/artist/{user_id} only as a
 * fallback when the caller didn't pass reach/impressions on submission.
 */
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { getCoverUrl, getProfilePhotoUrl, resolveShareCover, resolveShareArtistPhoto } from "@/lib/mediaUrls";
import { getPublicAppUrl, getPublicShareUrl } from "@/lib/publicUrl";
import { bakeAll, exportNodeToBlob, downloadBlob, shareOrDownload } from "@/lib/shareExport";
import NowFeaturedCard from "@/components/NowFeaturedCard";
import ShareChannels from "@/components/share/ShareChannels";

const LAYOUTS = [
  { id: "story",  label: "Story",  size: "1080 × 1920", w: 1080, h: 1920, hint: "Instagram / TikTok story" },
  { id: "square", label: "Post",   size: "1080 × 1080", w: 1080, h: 1080, hint: "IG / Twitter / Facebook" },
  { id: "wide",   label: "Header", size: "1600 × 900",  w: 1600, h: 900,  hint: "X header / site banner" },
];

const NowFeaturedShareModal = ({ open, onOpenChange, submission, artist }) => {
  const [layout, setLayout] = useState("story");
  const [exporting, setExporting] = useState(false);
  const [baked, setBaked] = useState({ cover: null, avatar: null });
  const [coverEmbedFailed, setCoverEmbedFailed] = useState(false);
  const [avatarEmbedFailed, setAvatarEmbedFailed] = useState(false);
  const [enrichedMetrics, setEnrichedMetrics] = useState(null);
  // Phase 15.4.1 — admin-editable share-card config per format.
  const [shareCardConfigs, setShareCardConfigs] = useState(null);
  const [renderedBlob, setRenderedBlob] = useState(null);  // iter_189 unified blob
  const cardRef = useRef(null);

  // Fetch the admin-edited share-card config once per modal open. Falls
  // back to local defaults (mergeConfig in NowFeaturedCard) if the
  // request fails so the modal never blanks.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/share-cards/config");
        if (!cancelled) setShareCardConfigs(r.data?.configs || null);
      } catch (_e) {
        if (!cancelled) setShareCardConfigs(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Pre-bake images whenever modal opens or layout changes (deterministic export).
  useEffect(() => {
    if (!open || !submission) return;
    let cancelled = false;
    setCoverEmbedFailed(false);
    setAvatarEmbedFailed(false);
    const run = async () => {
      // iter_188 — Use the canonical resolveShareCover / resolveShareArtistPhoto
      // helpers so EVERY known field variant is checked. Prior code only
      // consulted cover_url + cover_art and blanked out for payloads that
      // stored the cover under coverArt, cover_image, track_cover, etc.
      const coverUrl = resolveShareCover({
        cover_url: submission.cover_url,
        cover_art: submission.cover_art,
        cover_image: submission.cover_image,
        cover_art_url: submission.cover_art_url,
        track_cover: submission.track_cover,
        image_url: submission.image_url,
        updated_at: submission.cover_updated_at || submission.updated_at,
      });
      const avatarUrl = resolveShareArtistPhoto({
        profile_photo: artist?.profile_photo || submission?.user_profile_photo,
        picture: artist?.picture || submission?.user_picture,
        avatar_url: artist?.avatar_url,
        artist_avatar: submission?.artist_avatar,
      });
      const r = await bakeAll({ cover: coverUrl, avatar: avatarUrl });
      if (cancelled) return;
      setBaked({ cover: r.cover, avatar: r.avatar });
      setCoverEmbedFailed(r.coverFellBack);
      setAvatarEmbedFailed(r.avatarFellBack);
    };
    run();
    return () => { cancelled = true; };
  }, [open, submission, artist]);

  // Enrich metrics from canonical endpoint if caller didn't supply them.
  useEffect(() => {
    if (!open || !submission?.user_id) return;
    if (Number(submission.boost_applied || 0) + Number(submission.floor_applied || 0) > 0) return;
    let cancelled = false;
    api.get(`/metrics/artist/${submission.user_id}`)
      .then((r) => { if (!cancelled) setEnrichedMetrics(r.data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [open, submission?.user_id, submission?.boost_applied, submission?.floor_applied]);

  // Phase 12.1 — Fetch the authoritative, server-decorated track doc so
  // the share preview always matches what the public rails show
  // (display_plays + promotional_plays + momentum_badge). Fixes the
  // 10-vs-0 inconsistency where some callers pass a sparse `submission`
  // object missing the decorated fields.
  const [canonicalTrack, setCanonicalTrack] = useState(null);
  useEffect(() => {
    if (!open || !submission?.submission_id) return;
    let cancelled = false;
    api.get(`/track/${submission.submission_id}`)
      .then((r) => {
        if (cancelled) return;
        const t = r.data?.submission;
        if (t) setCanonicalTrack(t);
      })
      .catch(() => { /* keep using whatever caller passed */ });
    return () => { cancelled = true; };
  }, [open, submission?.submission_id]);

  // iter_189 — bake once and reuse the blob for every share channel.
  // Must live BEFORE the early return so hooks stay in stable order.
  useEffect(() => {
    if (!open || !submission || !baked.cover) return undefined;
    let cancelled = false;
    (async () => {
      // Yield for a paint cycle so the baked <img src=data:…> URIs are
      // applied to the offscreen NowFeaturedCard before we capture it.
      await new Promise((r) => setTimeout(r, 0));
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (cancelled || !cardRef.current) return;
      const b = await exportNodeToBlob(cardRef.current);
      if (!cancelled) setRenderedBlob(b);
    })();
    return () => { cancelled = true; };
  }, [open, layout, baked.cover, baked.avatar, submission]);

  if (!submission) return null;

  const reach =
    Number(submission.boost_applied || 0) + Number(submission.floor_applied || 0)
    || Number(enrichedMetrics?.reach || 0);
  const impressions = Number(submission.impressions || 0) || reach * 8;
  // Phase 12.1 — prefer the canonical fetch (always decorated) over the
  // sparse `submission` prop. This is what fixes the "10 vs 0" plays
  // discrepancy between surfaces and share preview.
  const authoritative = canonicalTrack || submission;
  const display_plays = Number(
    authoritative.display_plays
      ?? ((authoritative.play_count || 0) + (authoritative.promotional_plays || 0))
  );
  const artist_handle =
    artist?.artist_name ||
    submission.artist_display_name ||
    submission.display_artist_name ||
    submission.artist_name ||
    "RGM Artist";
  // Phase 11.0 — top 2 badges for the share card, keeps the celebrity
  // identity. Source: artist profile -> submission user_badges fallback.
  const badge_priority = [
    "founder", "founding_artist", "verified_artist", "spotlight_winner",
    "spotlight_artist", "playlist_pick", "admin",
  ];
  const all_badges = Array.isArray(artist?.badges) ? artist.badges
                   : Array.isArray(submission.user_badges) ? submission.user_badges
                   : [];
  const top_badges = badge_priority.filter((b) => all_badges.includes(b)).slice(0, 2);
  const share_url =
    submission.submission_id
      ? getPublicShareUrl(`/track/${submission.submission_id}`)
      : getPublicAppUrl();

  const data = {
    track_title: submission.track_title || "Untitled",
    artist_handle,
    reach,
    impressions,
    display_plays,
    badges: top_badges,
    // iter_188 — Canonical resolvers on the data object too so the
    // *visible* preview (raw URL) and the *exported* preview (baked
    // data URI) come from the same source. Previously getCoverUrl only
    // checked two fields, so payloads storing the cover under other
    // aliases (cover_image, track_cover, cover_art_url) would render
    // blank on the modal even before the bake stage.
    cover: resolveShareCover({
      cover_url: submission.cover_url,
      cover_art: submission.cover_art,
      cover_image: submission.cover_image,
      cover_art_url: submission.cover_art_url,
      track_cover: submission.track_cover,
      image_url: submission.image_url,
      updated_at: submission.cover_updated_at || submission.updated_at,
    }),
    avatar: resolveShareArtistPhoto({
      profile_photo: artist?.profile_photo,
      picture: artist?.picture,
      avatar_url: artist?.avatar_url,
    }),
    share_url,
  };

  const exportBlob = async () => {
    if (!cardRef.current) return null;
    return await exportNodeToBlob(cardRef.current);
  };

  const filenameFor = () => {
    const safe = (submission?.track_title || "track")
      .replace(/[^a-z0-9-_ ]/gi, "").trim() || "feature";
    return `RGM-Featured-${safe}-${layout}.png`;
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const blob = await exportBlob();
      if (!blob) throw new Error("export-failed");
      downloadBlob(blob, filenameFor());
      toast.success("Share card saved 🎉");
    } catch (_e) {
      toast.error("Couldn't render the card. Try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleNativeShare = async () => {
    setExporting(true);
    try {
      const blob = await exportBlob();
      if (!blob) throw new Error("export-failed");
      const result = await shareOrDownload({
        blob,
        filename: filenameFor(),
        title: "🎖️ Now Featured on RGM Studio",
        text: `My track "${data.track_title}" is in rotation on RGM Studio 🚀`,
        url: share_url,
      });
      if (result === "shared") toast.success("Shared!");
      else if (result === "downloaded") toast.success("Saved 🎉");
      else toast.error("Share isn't available here — try Download instead.");
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Share isn't available here — try Download instead.");
      }
    } finally {
      setExporting(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `🎖️ "${data.track_title}" is Now Featured on RGM Studio 🚀\n${share_url}`
      );
      toast.success("Link + caption copied");
    } catch (_e) {
      toast.error("Couldn't copy link");
    }
  };

  const activeLayout = LAYOUTS.find((l) => l.id === layout) || LAYOUTS[0];
  // Visible preview is scaled down to fit the modal.
  const previewMaxW = 360;
  const previewScale = previewMaxW / activeLayout.w;
  const previewHeight = activeLayout.h * previewScale;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-black border border-amber-400/40 max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="now-featured-share-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-lg">
            <span>🎖️</span>
            <span>Share Your Feature</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-amber-200/70">
            Premium social card — ready to post on IG, X, TikTok, Threads.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-1">
          {/* Layout picker */}
          <div className="grid grid-cols-3 gap-2 mt-2" data-testid="now-featured-layout-picker">
            {LAYOUTS.map((l) => (
              <button
                key={l.id}
                onClick={() => setLayout(l.id)}
                data-testid={`now-featured-layout-${l.id}`}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  layout === l.id
                    ? "border-amber-400/70 bg-amber-500/10"
                    : "border-gray-700 hover:border-amber-400/30"
                }`}
              >
                <div className="text-white font-bold text-sm">{l.label}</div>
                <div className="text-[10px] text-amber-200/70 mt-0.5">{l.size}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{l.hint}</div>
              </button>
            ))}
          </div>

          {/* Preview (scaled) */}
          <div className="mt-4 flex items-center justify-center bg-black/40 rounded-xl border border-amber-400/20 p-3" data-testid="now-featured-preview-frame">
            <div
              style={{
                width: previewMaxW,
                height: previewHeight,
                position: "relative",
                overflow: "hidden",
                borderRadius: 12,
              }}
            >
              <div
                style={{
                  width: activeLayout.w,
                  height: activeLayout.h,
                  transform: `scale(${previewScale})`,
                  transformOrigin: "top left",
                }}
              >
                <NowFeaturedCard
                  layout={layout}
                  data={data}
                  bakedCover={baked.cover}
                  bakedAvatar={baked.avatar}
                  config={shareCardConfigs?.[layout]}
                />
              </div>
            </div>
          </div>

          {/* Stats summary */}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-amber-100/80">
            <div className="px-3 py-2 rounded-md bg-amber-500/10 border border-amber-400/30">
              🏆 Reach <span className="text-white font-bold ml-1">{reach.toLocaleString()}</span>
            </div>
            <div className="px-3 py-2 rounded-md bg-pink-500/10 border border-pink-400/30">
              👁 Impressions <span className="text-white font-bold ml-1">{impressions.toLocaleString()}</span>
            </div>
          </div>

          {coverEmbedFailed && (
            <div
              className="mt-3 p-2.5 rounded-md bg-amber-500/15 border border-amber-400/40 text-[11px] text-amber-100"
              data-testid="now-featured-cover-warning"
            >
              ⚠️ Cover art couldn't be embedded — your PNG will show the branded RGM placeholder instead. Re-upload the cover from the track edit page to fix.
            </div>
          )}
          {avatarEmbedFailed && (
            <div
              className="mt-3 p-2.5 rounded-md bg-amber-500/15 border border-amber-400/40 text-[11px] text-amber-100"
              data-testid="now-featured-avatar-warning"
            >
              ⚠️ Profile photo couldn't be embedded — your PNG will show the default RGM avatar. Re-upload your profile photo to fix.
            </div>
          )}
        </div>

        {/* Actions — unified Share Channels row */}
        <div className="pt-3 mt-2 border-t border-amber-400/20 space-y-2">
          <ShareChannels
            blob={renderedBlob}
            filename={filenameFor()}
            title={`🎖️ Now Featured — ${data.track_title}`}
            text={`🎖️ "${data.track_title}" is Now Featured on RGM Studio 🚀`}
            url={share_url}
            testIdPrefix="now-featured-share"
          />
          {exporting && (
            <p className="text-[11px] text-amber-300/80 mt-1">Rendering preview…</p>
          )}
        </div>

        {/* Offscreen export target (full-size, used by html-to-image) */}
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: -99999,
            top: 0,
            width: activeLayout.w,
            height: activeLayout.h,
            pointerEvents: "none",
            opacity: 0.01,
          }}
        >
          <NowFeaturedCard
            ref={cardRef}
            layout={layout}
            data={data}
            bakedCover={baked.cover}
            bakedAvatar={baked.avatar}
            config={shareCardConfigs?.[layout]}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NowFeaturedShareModal;
